import React from 'react';
import { onPointImageError } from '../pointImageUtils';

const StarIcon = () => (
  <svg className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const PinIcon = () => (
  <svg className="w-3 h-3 text-emerald-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const PersonIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const TrendingCard = React.memo(({ place, onViewDetails, className = '' }) => (
  <article
    className={`rounded-xl border border-gray-100 bg-white overflow-hidden shadow-sm hover:shadow-lg transition-shadow group ${className}`}
  >
    {/* ── Image ── */}
    <div className="relative aspect-[4/3] overflow-hidden">
      <img
        src={place.imageUrl}
        alt={place.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        loading="lazy"
        onError={onPointImageError}
      />

      {/* Heart button */}
      <button
        type="button"
        className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white shadow flex items-center justify-center hover:scale-110 transition-transform"
        aria-label="Save place"
      >
        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
        </svg>
      </button>

      {/* Season tag (e.g. "May to Sep") */}
      {place.seasonTag && (
        <span className="absolute top-2.5 left-2.5 bg-emerald-600 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full shadow-sm">
          {place.seasonTag}
        </span>
      )}

      {/* Rating badge */}
      <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-lg px-2 py-1">
        <StarIcon />
        <span className="text-white font-bold text-xs">{place.rating}</span>
        <StarIcon />
        <span className="text-white/80 text-xs">{(place.reviewsCount ?? 0).toLocaleString()}</span>
      </div>
    </div>

    {/* ── Content ── */}
    <div className="p-3">
      <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-1">
        {place.title}
      </h3>

      <p className="flex items-center gap-1.5 text-xs text-gray-500 mt-1.5">
        <PinIcon />
        <span className="line-clamp-1">{place.locationText}</span>
      </p>

      {place.infoText && (
        <p className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1 line-clamp-1">
          <ClockIcon />
          <span>{place.infoText}</span>
        </p>
      )}

      {/* ── Action bar ── */}
      <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-gray-100">
        <button
          type="button"
          onClick={() => onViewDetails(place)}
          className="bg-emerald-700 hover:bg-emerald-800 text-white text-[11px] font-medium px-3 py-1.5 rounded transition-colors"
        >
          View Details
        </button>

        {place.categoryTag && (
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-600 border border-gray-200 rounded px-2 py-1">
            {place.categoryTag}
          </span>
        )}

        <button
          type="button"
          className="ml-auto w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-emerald-600 hover:border-emerald-300 transition-colors"
          aria-label="Profile"
        >
          <PersonIcon />
        </button>
      </div>
    </div>
  </article>
));

TrendingCard.displayName = 'TrendingCard';

export default TrendingCard;
